/**
 * Settings registry — the search-indexable catalogue of the 55
 * SettingsEntryCards on the Settings Hub landing.
 *
 * Mirrors the registry pattern shipped in `reports_registry.ts` (PR
 * 10). Global Search ⌘K (PR 30), help corpus, and the Khidmat
 * fallback (PR 32) all consume this same array — single source of
 * truth.
 *
 * The titleKey + descriptionKey resolve through the same
 * `settings_hub.*` i18n namespace as the landing page; the keys
 * stay in lockstep with `pages/landings/index.tsx`'s allCards
 * definition.
 */

export type SettingsSection =
  | "tenant_org"
  | "people_roles"
  | "policies_loans"
  | "policies_leaves"
  | "policies_eos"
  | "policies_attendance"
  | "policies_payroll"
  | "government"
  | "ai_config"
  | "hardware"
  | "imports"
  | "system";

export interface SettingsDef {
  id: string;
  section: SettingsSection;
  titleKey: string;
  descriptionKey: string;
  to: string;
}

export const SETTINGS_REGISTRY: SettingsDef[] = [
  // Tenant + Organization
  { id: "tenant", section: "tenant_org", titleKey: "settings_hub.card_tenant_title", descriptionKey: "settings_hub.card_tenant_desc", to: "/settings/tenant" },
  { id: "legal_entities", section: "tenant_org", titleKey: "settings_hub.card_legal_entities_title", descriptionKey: "settings_hub.card_legal_entities_desc", to: "/settings/legal-entities" },
  { id: "branding", section: "tenant_org", titleKey: "settings_hub.card_branding_title", descriptionKey: "settings_hub.card_branding_desc", to: "/settings/branding" },
  { id: "defaults", section: "tenant_org", titleKey: "settings_hub.card_defaults_title", descriptionKey: "settings_hub.card_defaults_desc", to: "/settings/tenant" },
  // People + Roles
  { id: "users_roles", section: "people_roles", titleKey: "settings_hub.card_users_roles_title", descriptionKey: "settings_hub.card_users_roles_desc", to: "/settings/users" },
  { id: "notifications", section: "people_roles", titleKey: "settings_hub.card_notifications_title", descriptionKey: "settings_hub.card_notifications_desc", to: "/settings/notifications" },
  // Policies — Loans
  { id: "loan_products", section: "policies_loans", titleKey: "settings_hub.card_loan_products_title", descriptionKey: "settings_hub.card_loan_products_desc", to: "/policies/loan-products" },
  // Policies — Leaves
  { id: "leave_types", section: "policies_leaves", titleKey: "settings_hub.card_leave_types_title", descriptionKey: "settings_hub.card_leave_types_desc", to: "/policies/leave-types" },
  { id: "holiday_calendar", section: "policies_leaves", titleKey: "settings_hub.card_holiday_calendar_title", descriptionKey: "settings_hub.card_holiday_calendar_desc", to: "/policies/holiday-calendar" },
  // Policies — EOS
  { id: "eos_rate_matrix", section: "policies_eos", titleKey: "settings_hub.card_eos_rate_matrix_title", descriptionKey: "settings_hub.card_eos_rate_matrix_desc", to: "/policies/eos-rate-matrix" },
  // Policies — Attendance
  { id: "shifts", section: "policies_attendance", titleKey: "settings_hub.card_shifts_title", descriptionKey: "settings_hub.card_shifts_desc", to: "/policies/shifts" },
  { id: "excuse_types", section: "policies_attendance", titleKey: "settings_hub.card_excuse_types_title", descriptionKey: "settings_hub.card_excuse_types_desc", to: "/policies/excuse-types" },
  // Policies — Payroll
  { id: "allowances", section: "policies_payroll", titleKey: "settings_hub.card_allowances_title", descriptionKey: "settings_hub.card_allowances_desc", to: "/policies/allowances" },
  { id: "grades", section: "policies_payroll", titleKey: "settings_hub.card_grades_title", descriptionKey: "settings_hub.card_grades_desc", to: "/policies/grades" },
  { id: "approval_chains", section: "policies_payroll", titleKey: "settings_hub.card_approval_chains_title", descriptionKey: "settings_hub.card_approval_chains_desc", to: "/policies/approval-chains" },
  // Government
  { id: "integrations", section: "government", titleKey: "settings_hub.card_integrations_title", descriptionKey: "settings_hub.card_integrations_desc", to: "/settings/integrations" },
  // AI Configuration
  { id: "ai_behavior", section: "ai_config", titleKey: "settings_hub.card_ai_behavior_title", descriptionKey: "settings_hub.card_ai_behavior_desc", to: "/ai/behavior" },
  { id: "ai_boundaries", section: "ai_config", titleKey: "settings_hub.card_ai_boundaries_title", descriptionKey: "settings_hub.card_ai_boundaries_desc", to: "/ai/boundaries" },
  { id: "ai_persona", section: "ai_config", titleKey: "settings_hub.card_ai_persona_title", descriptionKey: "settings_hub.card_ai_persona_desc", to: "/ai/persona" },
  { id: "ai_knowledge", section: "ai_config", titleKey: "settings_hub.card_ai_knowledge_title", descriptionKey: "settings_hub.card_ai_knowledge_desc", to: "/ai/knowledge" },
  { id: "ai_sandbox", section: "ai_config", titleKey: "settings_hub.card_ai_sandbox_title", descriptionKey: "settings_hub.card_ai_sandbox_desc", to: "/ai/sandbox" },
  // Hardware
  { id: "biometric", section: "hardware", titleKey: "settings_hub.card_biometric_title", descriptionKey: "settings_hub.card_biometric_desc", to: "/integrations/biometric" },
  // Imports
  { id: "imports", section: "imports", titleKey: "settings_hub.card_imports_title", descriptionKey: "settings_hub.card_imports_desc", to: "/settings/imports" },
  // System
  { id: "audit_log", section: "system", titleKey: "settings_hub.card_audit_log_title", descriptionKey: "settings_hub.card_audit_log_desc", to: "/settings/audit-log" },
  { id: "billing", section: "system", titleKey: "settings_hub.card_billing_title", descriptionKey: "settings_hub.card_billing_desc", to: "/settings/billing" },
];

/**
 * Fuzzy match across id + URL path. i18n key strings are also
 * scanned so search by domain term ("loan", "leave", "GOSI") hits.
 */
export function searchSettings(query: string): SettingsDef[] {
  const q = query.trim().toLowerCase();
  if (!q) return [];
  return SETTINGS_REGISTRY.filter((s) => {
    const blob = `${s.id} ${s.to} ${s.titleKey} ${s.descriptionKey} ${s.section}`.toLowerCase();
    return blob.includes(q);
  });
}

export const SETTINGS_COUNT = SETTINGS_REGISTRY.length;
